// src/layouts/RightRail.tsx
// The contextual panel and the strip of icons that opens it.
//
// The strip is the IDE arrangement: a permanent column of tool-window buttons
// at the edge of the window, so the panel is a place you know about even when
// the page in front has nothing to put in it. Tabs with no data are greyed out
// rather than removed — a row of tabs that appears and disappears is one people
// stop looking for. Labels live in tooltips because five of them do not fit in
// a 280px panel.

import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { cn } from '@core/ui';
import { railStore } from '@/stores/RailStore';
import { entityStore } from '@/stores/EntityStore';
import { tabsStore } from '@/stores/TabsStore';
import { Badge } from '@react/components';
import type { RailTab } from '@/features/layout/types';
import { getEntityPanels } from '@/features/layout/entityPanels';
import { EntityPanelBadges, panelBadgeStore } from '@/features/layout/entityPanelBadges';
import { PanelErrorBoundary } from '@/features/layout/PanelErrorBoundary';
// Side-effect import: registers the Discussion / Tasks / Schedule panels.
import '@/features/layout/entityPanelRegistrations';
import { groupResourcesByRelation, entityToRoute } from '@/features/resources/logic';
import { unlinkResources } from '@/features/resources/api';
import { ResourceManagePanel } from '@/features/resources/ResourceManagePanel';
import type { ResourceCardData } from '@/features/resources/types';
import type { AspectCardData } from '@/features/aspects/types';
import { toAspectCardData } from '@/features/aspects/logic';
import { ResourceList } from '@/components/ResourceList';
import { ResourceCommandMenu } from '@/components/ResourceCommandMenu';
import { AspectPanel } from '@/components/AspectPanel';
import { ServiceRailPanel } from '@/components/services/ServiceRailPanel';
import { Tooltip } from '@/components/Tooltip';
import type { Resource } from '@core/types/resource';

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function LinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
    </svg>
  );
}

function LayersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  );
}

function BoxIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
      <path d="m3.3 7 8.7 5 8.7-5" />
      <path d="M12 22V12" />
    </svg>
  );
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.912 5.813a2 2 0 001.275 1.275L21 12l-5.813 1.912a2 2 0 00-1.275 1.275L12 21l-1.912-5.813a2 2 0 00-1.275-1.275L3 12l5.813-1.912a2 2 0 001.275-1.275L12 3z" />
    </svg>
  );
}

function BoltIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

const TABS: { id: RailTab; icon: React.FC<{ className?: string }>; labelKey: string }[] = [
  { id: 'actions', icon: BoltIcon, labelKey: 'layout.rail.actions' },
  { id: 'resources', icon: LinkIcon, labelKey: 'layout.rail.resources' },
  { id: 'aspects', icon: LayersIcon, labelKey: 'layout.rail.aspects' },
  { id: 'services', icon: BoxIcon, labelKey: 'layout.rail.services' },
  { id: 'ai', icon: SparklesIcon, labelKey: 'layout.rail.ai' },
];

// Built-in tabs first, then whatever entity panels features registered
// (Discussion, Tasks, Schedule, …) — every detail page gets them once it puts
// its entity into context, no per-page wiring.
const ALL_TABS: { id: RailTab; icon: React.FC<{ className?: string }>; labelKey: string }[] = [
  ...TABS,
  ...getEntityPanels().map((p) => ({ id: p.key as RailTab, icon: p.icon, labelKey: p.labelKey })),
];

export const RightRail = observer(() => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const dragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const [resourceMenuOpen, setResourceMenuOpen] = useState(false);
  const [resourceMenuRelationType, setResourceMenuRelationType] = useState<string | undefined>();
  const [manageOpen, setManageOpen] = useState(false);

  const onPointerMove = useCallback((e: PointerEvent) => {
    if (!dragging.current) return;
    const delta = startX.current - e.clientX;
    railStore.resize(startWidth.current + delta);
  }, []);

  const onPointerUp = useCallback(() => {
    dragging.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', onPointerUp);
  }, [onPointerMove]);

  const onResizeStart = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    dragging.current = true;
    startX.current = e.clientX;
    startWidth.current = railStore.width;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
  }, [onPointerMove, onPointerUp]);

  // The rail always speaks for the tab in front, never for whichever mounted
  // pane rendered last.
  const tabId = tabsStore.activeTabId;
  const currentEntity = entityStore.currentEntity;
  const pageActions = railStore.pageActions;
  const pageSettings = railStore.pageSettings;

  // --- Handlers ---
  const handleNavigateToResource = (data: ResourceCardData) => {
    const route = entityToRoute({ type_name: data.typeName, type_id: data.typeId, id: data.id });
    if (route) navigate(route);
  };

  const handleUnlinkResource = (resourceId: string) => {
    const resource = entityStore.resources.find((r) => r.entity?.id === resourceId);
    // Optimistic remove; restore on failure since the request is fire-and-forget from the UI's view.
    entityStore.removeResourceOptimistic(tabId, resourceId);
    if (currentEntity && resource?.urn) {
      unlinkResources(
        { urn: currentEntity.urn } as any,
        { urn: resource.urn } as any,
        resource.relation_type,
      ).catch(() => {
        entityStore.addResourceOptimistic(tabId, resource);
      });
    }
  };

  const handleResourceLinked = (resource: Resource) => {
    entityStore.addResourceOptimistic(tabId, resource);
  };

  const handleNavigateToAspect = (data: AspectCardData) => {
    const route = entityToRoute({ type_name: data.typeName, type_id: data.typeId, id: data.id });
    if (route) navigate(route);
  };

  // Public method for opening resource menu with preset relation type (used by shortcuts)
  const openResourceMenu = (relationType?: string) => {
    setResourceMenuRelationType(relationType);
    setResourceMenuOpen(true);
  };

  // Expose for keyboard shortcuts
  (window as any).__railOpenResourceMenu = openResourceMenu;

  const badgeForTab = (tab: RailTab): number => {
    if (tab === 'resources') return entityStore.totalResources;
    if (tab === 'aspects') return entityStore.totalAspects;
    return panelBadgeStore.get(tab);
  };

  const aspectCardData = entityStore.aspects.map(toAspectCardData);

  // A tab has something to show only when its source exists: the page's
  // settings and its quick actions come from the page, everything else from the
  // entity in context.
  const hasActions = pageActions.length > 0 || pageSettings !== null;
  const hasData = (tab: RailTab): boolean => {
    if (tab === 'actions') return hasActions;
    if (currentEntity === null) return false;
    const panel = getEntityPanels().find((p) => p.key === tab);
    if (panel?.appliesTo && !panel.appliesTo.includes(currentEntity.type)) return false;
    if (panel?.excludesTypes?.includes(currentEntity.type)) return false;
    return true;
  };

  const anyData = ALL_TABS.some((tab) => hasData(tab.id));
  const activeTab = hasData(railStore.activeTab)
    ? railStore.activeTab
    : ALL_TABS.find((tab) => hasData(tab.id))?.id ?? railStore.activeTab;

  // With nothing to show, the header names the panel rather than a tab that
  // has no content behind it.
  const activeLabelKey = anyData
    ? ALL_TABS.find((tab) => tab.id === activeTab)?.labelKey ?? 'layout.rail.label'
    : 'layout.rail.label';

  // The registered entity panel behind the active tab, if it is one of them.
  const activeEntityPanel = getEntityPanels().find((p) => p.key === activeTab);

  const handleStripClick = (tab: RailTab) => {
    // Clicking the tab already in front is how the panel is put away, exactly
    // as in the tool-window strip this mirrors.
    if (railStore.isOpen && activeTab === tab) railStore.close();
    else railStore.setTab(tab);
  };

  const iconStrip = (
    <div
      role="tablist"
      aria-orientation="vertical"
      aria-label={t('layout.rail.label')}
      className="hidden xl:flex w-9 flex-shrink-0 flex-col items-center gap-1 border-l border-rail-border bg-rail py-2 z-20"
    >
      {ALL_TABS.filter((tab) => {
        const panel = getEntityPanels().find((item) => item.key === tab.id);
        return !panel || hasData(tab.id);
      }).map((tab) => {
        const enabled = hasData(tab.id);
        const selected = railStore.isOpen && activeTab === tab.id;
        const count = badgeForTab(tab.id);
        const label = t(tab.labelKey);
        return (
          <Tooltip
            key={tab.id}
            side="left"
            label={enabled ? label : `${label} — ${t('layout.rail.noData')}`}
          >
            <button
              type="button"
              role="tab"
              // aria-disabled rather than the `disabled` attribute: a disabled
              // button swallows the hover the tooltip needs, and the tooltip is
              // the only thing that explains why the tab is greyed out.
              aria-disabled={!enabled}
              aria-selected={selected}
              aria-label={label}
              onClick={() => enabled && handleStripClick(tab.id)}
              className={cn(
                'relative flex h-7 w-7 items-center justify-center rounded-md transition-colors',
                !enabled
                  ? 'text-muted-foreground/40 cursor-default'
                  : selected
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:text-rail-foreground hover:bg-accent/50',
              )}
            >
              <tab.icon className="h-4 w-4" />
              {enabled && count > 0 && (
                <Badge
                  variant="primary"
                  size="sm"
                  className="pointer-events-none absolute -right-1 -top-1 min-w-[15px] justify-center px-1 py-0 text-[10px] leading-[15px]"
                >
                  {count}
                </Badge>
              )}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );

  const panelHeader = (
    <div className="flex items-center justify-between h-9 pl-3 pr-1.5 border-b border-rail-border flex-shrink-0">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground truncate">
        {t(activeLabelKey)}
      </span>
      <button
        onClick={() => railStore.close()}
        className="p-1 rounded-md text-muted-foreground hover:text-rail-foreground hover:bg-accent/50 transition-colors"
        aria-label={t('layout.rail.close')}
      >
        <XIcon className="h-4 w-4" />
      </button>
    </div>
  );

  // The page's settings, expanded, above the things you do to it. Same sections
  // as the navbar's menu folds away — this is the surface with the height to
  // show them all at once, which is the reason to open the panel at all.
  const actionsPanel = (
    <div className="space-y-1">
      {pageSettings}

      {pageSettings !== null && pageActions.length > 0 && (
        <div className="!mt-3 border-t border-rail-border pt-2" />
      )}

      {pageActions.map((action) => (
        <button
          key={action.key}
          type="button"
          onClick={action.onSelect}
          className="w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-rail-foreground hover:bg-accent/50 transition-colors text-left focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent/40 text-muted-foreground" aria-hidden="true">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d={action.icon} />
            </svg>
          </span>
          {action.label}
        </button>
      ))}
    </div>
  );

  const tabContent = (
    <div className={activeEntityPanel
      ? 'flex min-h-0 flex-1 flex-col overflow-hidden p-3'
      : 'flex-1 overflow-y-auto p-3'
    }>
      {activeTab === 'actions' && hasActions ? (
        actionsPanel
      ) : !currentEntity ? (
        <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
          {t('layout.rail.noContext')}
        </div>
      ) : (
        <>
          {activeTab === 'resources' && (
            <ResourceList
              groups={groupResourcesByRelation(entityStore.resources)}
              isLoading={entityStore.isLoadingResources}
              onUnlink={handleUnlinkResource}
              onNavigate={handleNavigateToResource}
              onAddClick={() => setResourceMenuOpen(true)}
              onManageClick={() => setManageOpen(true)}
            />
          )}
          {activeTab === 'aspects' && (
            <AspectPanel
              aspects={aspectCardData}
              isLoading={entityStore.isLoadingAspects}
              onNavigate={handleNavigateToAspect}
            />
          )}
          {activeTab === 'services' && (
            <ServiceRailPanel />
          )}
          {activeTab === 'ai' && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-rail-foreground">
                {t('layout.rail.ai')}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('layout.rail.aiPlaceholder')}
              </p>
            </div>
          )}
          {activeEntityPanel && (
            <PanelErrorBoundary
              label={t(activeEntityPanel.labelKey)}
              resetKey={activeTab}
            >
              <activeEntityPanel.component
                entity={{ id: currentEntity.id, type_name: currentEntity.type }}
                title={currentEntity.title}
              />
            </PanelErrorBoundary>
          )}
        </>
      )}
    </div>
  );

  return (
    <>
      {/* Headless badge counters for entity-panel tabs (unread discussions…) */}
      <EntityPanelBadges ctx={currentEntity} />

      {/* Mobile / Tablet: drawer overlay. No icon strip below xl — there is no
          room for a permanent column next to a full-width page. */}
      {railStore.isOpen && (
        <div className="xl:hidden fixed inset-0 z-40">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => railStore.close()}
            aria-hidden="true"
          />

          {/* Drawer panel — slides in from right */}
          <aside
            aria-label={t('layout.rail.label')}
            // Full width on a phone: a 320px drawer over a 390px screen leaves a
            // sliver of page that is neither readable nor dismissible by aiming.
            className="absolute inset-y-0 right-0 flex w-full flex-col border-l border-rail-border bg-rail shadow-lg animate-in slide-in-from-right duration-200 sm:w-80 sm:max-w-[calc(100vw-3rem)]"
          >
            {panelHeader}
            {tabContent}
          </aside>
        </div>
      )}

      {/* Desktop: inline panel, with the strip pinned to the window edge */}
      {railStore.isOpen && (
        <aside
          aria-label={t('layout.rail.label')}
          className="relative hidden xl:flex flex-col h-full bg-rail border-l border-rail-border z-20"
          style={{ width: railStore.width }}
        >
          {/* Resize handle */}
          <div
            onPointerDown={onResizeStart}
            className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/30 active:bg-primary/50 transition-colors z-30"
          />
          {panelHeader}
          {tabContent}
        </aside>
      )}

      {iconStrip}

      {/* Resource command menu (portaled above everything) */}
      <ResourceCommandMenu
        isOpen={resourceMenuOpen}
        onClose={() => { setResourceMenuOpen(false); setResourceMenuRelationType(undefined); }}
        entityUrn={currentEntity?.urn || ''}
        entityType={currentEntity?.type || ''}
        defaultRelationType={resourceMenuRelationType}
        onLinked={handleResourceLinked}
      />
      {manageOpen && currentEntity && (
        <ResourceManagePanel
          entity={currentEntity}
          onClose={() => setManageOpen(false)}
          onChanged={() => entityStore.loadResources(tabId, currentEntity)}
        />
      )}
    </>
  );
});
